import * as React from 'react';
import { useEffect, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';
import AppForm from '../../common/AppForm';
import PageTitle from '../../common/PageTitle';
import { getDirectorsData, getOfficesByCountry } from '../../../../lib/utils';
import { RegisterV2Model } from '../../../../models/AccountModels';
import {
	getRegistrationDataFromLocalStorage,
	setAppUserOfficeCountryToLocalStorage,
	setRegistrationDataToLocalStorage
} from '../../../../core/localStorage';
import { directorsQueryKey, officesQueryKey, registerV2User, useDirectors, useOffices } from '../../../../core/api';
import DropdownListWithModalField from '../../../../commonComponents/appForm/DropdownListWithModalField';
import { DropDownListOption } from '../../../../models/DropDownListOption';
import { ApiResultMessages } from '../../../../core/enums';
import BottomModal from '../../../../commonComponents/bottomModal/BottomModal';
import { ApiResultMessageModel, getAndValidateApiMessage } from '../../../../models/ApiResultMessage';
import { defaultModalMessage, isMcProgressApp, isBetterOffApp } from '../../../../core/domain';
import { hasValue } from '../../../../lib/lib';
import getAppVariantConfig from '../../../../appVariants/appVariantConfig';

const OfficePartnerForm = ({ onSubmit }: Props) => {
	const queryClient = useQueryClient();
	const methods = useForm();
	const { reset, control, setValue, getValues } = methods;
	const officeId = useWatch({ control, name: 'officeId', defaultValue: null });
	const selectedCountry = useWatch({ control, name: 'country', defaultValue: null });
	const [modalMessage, setModalMessage] = useState<ApiResultMessageModel>({
		title: '',
		description: []
	});
	const offices = useOffices() ?? [];
	const directors = useDirectors() ?? [];
	const [directorsData, setDirectorsData] = useState<DropDownListOption<number>[]>([]);
	const { userCountriesData } = getAppVariantConfig();

	const handleCloseModal = () => {
		setModalMessage(defaultModalMessage);
	};

	useEffect(() => {
		const storedData = localStorage.getItem('registerData');
		if (storedData) {
			const parsedData: RegisterV2Model = JSON.parse(storedData);
			reset(parsedData);
		}
	}, []);

	useEffect(() => {
		const newDirectorsData = getDirectorsData(
			directors.filter((d) => (isMcProgressApp() ? d.hasMcClients : true)),
			officeId
		);
		setDirectorsData(newDirectorsData);

		const directorId = getValues('directorId');
		if (!newDirectorsData.some((x) => x.value === directorId)) {
			setValue('directorId', null);
		}
	}, [officeId, directors]);

	useEffect(() => {
		setValue('officeId', null);
		setValue('directorId', null);
		if (selectedCountry) {
			// Store the selected country directly
			setAppUserOfficeCountryToLocalStorage(selectedCountry);
			// Invalidate and refetch offices and directors to get data from the correct country API (AU/US)
			queryClient.invalidateQueries({ queryKey: officesQueryKey });
			queryClient.invalidateQueries({ queryKey: directorsQueryKey });
		}
	}, [selectedCountry, setValue, queryClient]);

	const handleSubmit = async (registerModel: RegisterV2Model) => {
		const storedData = getRegistrationDataFromLocalStorage();
		const existingData: RegisterV2Model = storedData ? JSON.parse(storedData) : {};

		// Update the register fields
		const updatedData = {
			...existingData,
			country: registerModel.country,
			officeId: registerModel.officeId,
			directorId: registerModel.directorId
		};
		setRegistrationDataToLocalStorage(JSON.stringify(updatedData));

		const apiResult = await registerV2User(updatedData);
		if (apiResult && !apiResult.succeeded) {
			if (apiResult.response === ApiResultMessages.AccountAlreadyExists) {
				const apiMessage = getAndValidateApiMessage(apiResult.response as string);
				setModalMessage(apiMessage);
			} else throw new Error(`${apiResult.response}`);
		} else {
			onSubmit();
		}
	};

	return (
		<>
			<PageTitle title="Select your office and Partner" />
			<AppForm methods={methods} onSubmit={handleSubmit} primaryButtonText="Continue" singleColumn>
				<DropdownListWithModalField
					label="Select country"
					modalTitle="Country"
					name="country"
					data={userCountriesData}
					validate={(value: string) => hasValue(value) || 'Please choose a country.'}
				/>
				<DropdownListWithModalField
					label="Select Kelly+Partners Office"
					modalTitle="Kelly+Partners Office"
					name="officeId"
					disabled={!selectedCountry}
					data={getOfficesByCountry(
						offices.filter((o) => (isMcProgressApp() ? o.hasMcClients : true)),
						selectedCountry
					)}
					validate={(value: string) => hasValue(value) || 'Please choose a Kelly+Partners office.'}
				/>
				{!isBetterOffApp() && (
					<DropdownListWithModalField
						label="Select Partner"
						modalTitle="Partner"
						name="directorId"
						disabled={!selectedCountry}
						data={directorsData}
						validate={(value: string) => hasValue(value) || 'Please choose a Kelly+Partners Partner.'}
					/>
				)}
				<BottomModal
					title={modalMessage.title}
					body={modalMessage.description}
					type={modalMessage.errorType}
					actions={modalMessage.actions}
					open={hasValue(modalMessage.title)}
					onClose={handleCloseModal}
				/>
			</AppForm>
		</>
	);
};

export default OfficePartnerForm;

interface Props {
	onSubmit?: () => void;
}
